import type { StateCreator } from 'zustand';
import type { StoreState } from '../useStore';
import type {
  TutorConcept,
  TutorCurriculumItem,
  TutorExercise,
  TutorMessage,
  TutorMode,
  TutorSession,
} from '@/types/tutor';
import { Rating } from 'ts-fsrs';
import * as tutorApi from '@/services/tutorApi';
import { buildTutorContext } from '@/utils/tutorContextBuilder';
import { scheduleConceptReview, computeMasteryLevel, isConceptDue } from '@/utils/tutorSrs';
import { sanitizeQuizQuestions } from '@/utils/quizValidation';
import { reportError } from '@/services/errorTracking';

export interface TutorSlice {
  tutorConcepts: TutorConcept[];
  curriculum: TutorCurriculumItem[];
  currentSession: TutorSession | null;
  messages: TutorMessage[];
  isTutorLoading: boolean;
  currentExercise: TutorExercise | null;
  setCurrentExercise: (exercise: TutorExercise | null) => void;
  setTutorConcepts: (concepts: TutorConcept[]) => void;
  setCurriculum: (items: TutorCurriculumItem[]) => void;
  setCurrentSession: (session: TutorSession | null) => void;
  setMessages: (messages: TutorMessage[]) => void;
  setIsTutorLoading: (loading: boolean) => void;
  loadTutorData: () => Promise<void>;
  startSession: (mode: TutorMode, conceptKey?: string) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  submitExercise: (
    exerciseId: string,
    results: import('@/types/quiz').QuizAnswerResult[],
  ) => Promise<void>;
  rateConcept: (conceptKey: string, rating: Rating) => Promise<void>;
  getDueConcepts: () => TutorConcept[];
  getNextConcept: () => TutorConcept | null;
}

export const createTutorSlice: StateCreator<StoreState, [], [], TutorSlice> = (set, get) => ({
  tutorConcepts: [],
  curriculum: [],
  currentSession: null,
  messages: [],
  isTutorLoading: false,
  currentExercise: null,
  setCurrentExercise: (currentExercise) => set({ currentExercise }),
  setTutorConcepts: (tutorConcepts) => set({ tutorConcepts }),
  setCurriculum: (curriculum) => set({ curriculum }),
  setCurrentSession: (currentSession) => set({ currentSession }),
  setMessages: (messages) => set({ messages }),
  setIsTutorLoading: (isTutorLoading) => set({ isTutorLoading }),

  loadTutorData: async () => {
    try {
      const [concepts, curriculum, sessions] = await Promise.all([
        tutorApi.fetchTutorConcepts(),
        tutorApi.fetchTutorCurriculum(),
        tutorApi.fetchTutorSessions(5),
      ]);
      set({
        tutorConcepts: concepts,
        curriculum,
        currentSession: sessions[0] ?? null,
        messages: sessions[0]?.messages ?? [],
      });
    } catch (err) {
      reportError(err, { operation: 'loadTutorData' });
    }
  },

  startSession: async (mode, conceptKey) => {
    set({ isTutorLoading: true });
    try {
      const session = await tutorApi.createTutorSession(mode, conceptKey);
      set({ currentSession: session, messages: [], isTutorLoading: false });
    } catch (err) {
      set({ isTutorLoading: false });
      reportError(err, { operation: 'startTutorSession' });
      throw err;
    }
  },

  sendMessage: async (content) => {
    const state = get();
    const session = state.currentSession;
    if (!session) return;
    const userMessage: TutorMessage = {
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };
    const updatedMessages = [...state.messages, userMessage];
    set({ messages: updatedMessages, isTutorLoading: true });
    void tutorApi
      .appendTutorMessage(session.id, userMessage)
      .catch((err) =>
        reportError(err, { operation: 'appendTutorMessage', extra: { sessionId: session.id } }),
      );

    try {
      const context = buildTutorContext({
        cefrLevel: state.settings.cefrLevel,
        cards: state.cards,
        grammarProgress: state.grammarProgress,
        tutorConcepts: state.tutorConcepts,
        curriculum: state.curriculum,
        recentSessions: state.currentSession ? [state.currentSession] : [],
        recentExercises: [],
      });
      const response = await tutorApi.callTutor({
        message: content,
        mode: session.mode,
        conceptKey: session.conceptKey ?? undefined,
        sessionId: session.id,
        context,
      });

      // Handle exercise payload — create a TutorExercise record and show the quiz
      // overlay. The tutor model occasionally emits malformed questions (missing
      // stem/sentenceTemplate/options); rendering those crashes the play area, so
      // we drop invalid ones here and only surface an exercise if any survive.
      const validQuestions = sanitizeQuizQuestions(response.exercise?.questions);
      const hasExercise = validQuestions.length > 0;
      if (response.exercise && hasExercise) {
        const exercise = await (async () => {
          try {
            return await tutorApi.createTutorExercise({
              sessionId: session.id,
              conceptKey: session.conceptKey ?? 'general',
              exerciseType: 'quiz',
              prompt: response.exercise!.instructions,
              questions: validQuestions,
              userAnswers: null,
              aiFeedback: null,
              score: null,
              completedAt: null,
            });
          } catch (err) {
            reportError(err, { operation: 'createTutorExercise' });
            return null;
          }
        })();
        if (exercise) {
          get().setCurrentExercise(exercise);
        }
      }

      const tutorMessage: TutorMessage = {
        role: 'tutor',
        content: hasExercise ? "I've prepared an exercise for you!" : response.response,
        timestamp: new Date().toISOString(),
      };
      const finalMessages = [...updatedMessages, tutorMessage];
      set({ messages: finalMessages, isTutorLoading: false });
      void tutorApi
        .appendTutorMessage(session.id, tutorMessage)
        .catch((err) =>
          reportError(err, { operation: 'appendTutorMessage', extra: { sessionId: session.id } }),
        );
      if (response.sessionSummary) {
        void tutorApi
          .updateTutorSession(session.id, { sessionSummary: response.sessionSummary })
          .catch((err) =>
            reportError(err, { operation: 'updateTutorSession', extra: { sessionId: session.id } }),
          );
      }
      for (const action of response.actions) await handleTutorAction(action, get);
    } catch (err) {
      set({ isTutorLoading: false });
      reportError(err, { operation: 'sendTutorMessage' });
      const errorMessage: TutorMessage = {
        role: 'tutor',
        content: 'Sorry, I had trouble processing that. Please try again in a moment.',
        timestamp: new Date().toISOString(),
      };
      set({ messages: [...updatedMessages, errorMessage] });
      void tutorApi
        .appendTutorMessage(session.id, errorMessage)
        .catch((err) =>
          reportError(err, { operation: 'appendTutorMessage', extra: { sessionId: session.id } }),
        );
    }
  },

  submitExercise: async (exerciseId, results) => {
    const state = get();
    const correctCount = results.filter((r) => r.isCorrect).length;
    const score = Math.round((correctCount / results.length) * 100);
    void tutorApi.updateTutorExercise(exerciseId, {
      userAnswers: results,
      score,
      completedAt: new Date().toISOString(),
    });
    set({ currentExercise: null });
    const sess = state.currentSession;
    if (sess) {
      const summary = `I completed the exercise with ${correctCount}/${results.length} correct (${score}%).`;
      await get().sendMessage(summary);
    }
  },

  rateConcept: async (conceptKey, rating) => {
    const state = get();
    const concept = state.tutorConcepts.find((c) => c.conceptKey === conceptKey);
    if (!concept) return;
    const now = new Date();
    const scheduled = scheduleConceptReview(concept, rating, now);
    const newMastery = computeMasteryLevel(concept.masteryLevel, rating);
    const updatedConcepts = state.tutorConcepts.map((c) =>
      c.conceptKey === conceptKey
        ? {
            ...c,
            srsState: scheduled.srsState,
            dueDate: scheduled.dueDate.toISOString(),
            lastReviewed: now.toISOString(),
            masteryLevel: newMastery,
          }
        : c,
    );
    set({ tutorConcepts: updatedConcepts });
    try {
      await tutorApi.upsertTutorConcept(conceptKey, {
        srsState: scheduled.srsState,
        dueDate: scheduled.dueDate.toISOString(),
        lastReviewed: now.toISOString(),
        masteryLevel: newMastery,
      });
    } catch (err) {
      set({ tutorConcepts: state.tutorConcepts });
      reportError(err, { operation: 'rateConcept' });
    }
  },

  getDueConcepts: () => {
    const now = new Date();
    return get().tutorConcepts.filter((c) => isConceptDue(c, now));
  },

  getNextConcept: () => {
    const state = get();
    const due = state.tutorConcepts.filter((c) => isConceptDue(c, new Date()));
    if (due.length > 0)
      return due.sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())[0];
    const pending = state.curriculum.filter((item) => item.status === 'pending');
    if (pending.length === 0) return null;
    const next = pending.sort((a, b) => a.sequenceOrder - b.sequenceOrder)[0];
    return state.tutorConcepts.find((c) => c.conceptKey === next.conceptKey) ?? null;
  },
});

async function handleTutorAction(
  action: import('@/types/tutor').TutorAction,
  get: () => StoreState,
): Promise<void> {
  switch (action.type) {
    case 'schedule_review': {
      const { conceptKey, dueDate } = action.payload as { conceptKey: string; dueDate: string };
      if (get().tutorConcepts.find((c) => c.conceptKey === conceptKey))
        await tutorApi.upsertTutorConcept(conceptKey, { dueDate });
      break;
    }
    case 'mark_mastered': {
      const { conceptKey } = action.payload as { conceptKey: string };
      if (get().tutorConcepts.find((c) => c.conceptKey === conceptKey))
        await tutorApi.upsertTutorConcept(conceptKey, { masteryLevel: 3, strength: 1 });
      break;
    }
    case 'update_curriculum': {
      const updates = action.payload as {
        conceptKey: string;
        status: string;
        sequenceOrder?: number;
      };
      const item = get().curriculum.find((c) => c.conceptKey === updates.conceptKey);
      if (item)
        await tutorApi.updateCurriculumItem(item.id, {
          status: updates.status as TutorCurriculumItem['status'],
          ...(updates.sequenceOrder !== undefined ? { sequenceOrder: updates.sequenceOrder } : {}),
        });
      break;
    }
    default:
      reportError(new Error(`Unknown tutor action: ${String(action.type)}`), {
        operation: 'tutor.handleAction',
        extra: { actionType: action.type },
      });
  }
}
